/**
 * DriveTab - phone-direct OBD-II driving vertical.
 *
 * Lifecycle:
 *   idle -> connect ELM327 -> start trip -> live readings -> end trip
 *      -> user picks or resolves a Vibe Node
 *      -> Save to Pal privately and/or Share Echo anonymously.
 */

import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { App as CapApp } from '@capacitor/app';
import { Geolocation } from '@capacitor/geolocation';
import { Share } from '@capacitor/share';
import { latLngToCell } from 'h3-js';
import { api, type ActivityDriverTripResponse, type PalProfileResponse, type RenderResponse } from '../cloud/api';
import { FaceState } from '../pal/face';
import {
    Elm327Driver,
    isWebBluetoothSupported,
    type DriverEvent,
} from '../drive/bt-elm327';
import { createDefaultElm327Driver } from '../drive/driver-factory';
import { isNative } from '../native/platform';
import {
    applyEvent,
    emptySnapshot,
    snapshotToSummary,
    type TripSnapshot,
} from '../drive/trip-session';
import { inferPersona } from '../drive/persona-inference';
import {
    distanceToBucket,
    rpmToBucket,
    speedToBucket,
    throttleToBucket,
} from '../drive/trip-privacy';
import { AmbientMode } from '../pal/ambient';
import { PalFaceCartoon } from './PalFaceCartoon';
import { palProfileToFaceProps, type FaceBaseProps } from './FacePropAdapter';
import { LandscapeDriveDisplay, shouldShowLandscapeCockpit } from './LandscapeDriveDisplay';
import { NowPlayingChip, NowPlayingPermissionCard } from './NowPlayingChip';
import { TripRouteMap } from './TripRouteMap';
import { useDriveFaceState } from './hooks/useDriveFaceState';
import { useMusicVibe } from './hooks/useMusicVibe';
import { useNowPlaying } from './hooks/useNowPlaying';
import { useOrientationMode } from './hooks/useOrientationMode';
import { useWakeLock } from './hooks/useWakeLock';
import { useVoiceOptIn } from './hooks/useVoiceOptIn';
import { useVoiceSession, type VoiceAppendTurn, type VoiceSessionStatus } from './hooks/useVoiceSession';

const VibeMap = lazy(() => import('./VibeMap').then((module) => ({ default: module.VibeMap })));

type DriverStatus = 'idle' | 'requesting' | 'connecting' | 'initialising' | 'connected' | 'disconnected' | 'error';

interface NodeChoice { id: string; name: string; }

type TripNodeTarget =
    | { kind: 'existing'; nodeId: string }
    | { kind: 'resolved'; lat: number; lng: number; name: string; locality?: string };

export function DriveTab({
    pal,
    active = true,
    renderVoiceTurn,
    appendVoiceTurn,
}: {
    pal: PalProfileResponse;
    active?: boolean;
    renderVoiceTurn?: (query: string) => Promise<RenderResponse>;
    appendVoiceTurn?: (turn: VoiceAppendTurn) => void;
}) {
    const supported = isNative() || isWebBluetoothSupported();
    const [status, setStatus] = useState<DriverStatus>('idle');
    const [statusMessage, setStatusMessage] = useState<string | null>(null);
    const [snapshot, setSnapshot] = useState<TripSnapshot>(emptySnapshot());
    const [echoBusy, setEchoBusy] = useState(false);
    const [activityBusy, setActivityBusy] = useState(false);
    const [activityResult, setActivityResult] = useState<ActivityDriverTripResponse | null>(null);
    const [echoResult, setEchoResult] = useState<{
        trace_id: string;
        completed_quests: Array<{ title: string; rewards?: { glow?: number; marks?: string[] } }>;
    } | null>(null);
    const driverRef = useRef<Elm327Driver | null>(null);
    const [showTroubleshoot, setShowTroubleshoot] = useState(false);
    const [debugLogs, setDebugLogs] = useState<string[]>([]);
    const [showDebug, setShowDebug] = useState(false);
    const [showVibeMap, setShowVibeMap] = useState(false);

    function ts() { return new Date().toISOString().slice(11, 23); }

    function handleDriverEvent(e: DriverEvent) {
        if (e.kind === 'status') {
            setStatus(e.status);
            setStatusMessage(e.message ?? null);
            setDebugLogs((prev) => [...prev.slice(-199), `[${ts()}] STATUS: ${e.status}${e.message ? ' - ' + e.message : ''}`]);
        }
        if (e.kind === 'line') {
            setDebugLogs((prev) => [...prev.slice(-199), `[${ts()}] ${e.raw.startsWith('[DBG]') ? e.raw : `RAW: ${e.raw}`}`]);
        }
        if (e.kind === 'parsed') {
            if (e.result.kind === 'reading') {
                const reading = e.result.reading;
                setSnapshot((prev) => applyEvent(prev, { kind: 'reading', reading }));
            } else {
                setDebugLogs((prev) => [...prev.slice(-199), `[${ts()}] PARSE: ${JSON.stringify(e.result)}`]);
            }
        }
    }

    async function shareLogs() {
        if (debugLogs.length === 0) return;
        try {
            await Share.share({
                title: 'OBD Debug Logs',
                text: debugLogs.join('\n'),
                dialogTitle: 'Share OBD logs',
            });
        } catch {
            // Share sheet dismissed.
        }
    }

    function attachDriver(driver: Elm327Driver) {
        const prev = driverRef.current;
        if (prev) void prev.disconnect();
        driverRef.current = driver;
        driver.on(handleDriverEvent);
    }

    useEffect(() => {
        return () => {
            const d = driverRef.current;
            if (d) void d.disconnect();
            driverRef.current = null;
        };
    }, []);

    useEffect(() => {
        if (status === 'connected' && snapshot.state === 'idle') {
            setSnapshot((prev) => applyEvent(prev, { kind: 'connected', at: nowSec() }));
        }
    }, [status, snapshot.state]);

    useEffect(() => {
        if (snapshot.state !== 'driving') return;
        const interval = setInterval(() => {
            setSnapshot((prev) => {
                if (prev.state !== 'driving' || !prev.started_at) return prev;
                return { ...prev, duration_s: nowSec() - prev.started_at };
            });
        }, 1000);
        return () => clearInterval(interval);
    }, [snapshot.state]);

    useEffect(() => {
        if (snapshot.state !== 'driving') return;

        let watchId: string | null = null;
        let cancelled = false;

        const startTracking = async () => {
            try {
                if (isNative()) {
                    const perm = await Geolocation.requestPermissions();
                    if (perm.location !== 'granted') {
                        console.warn('Location permission denied');
                        return;
                    }
                }

                if (cancelled) return;

                watchId = await Geolocation.watchPosition(
                    { enableHighAccuracy: true, maximumAge: 5000, timeout: 10000 },
                    (pos, err) => {
                        if (err) {
                            console.warn('GPS watch error', err);
                            return;
                        }
                        if (pos) {
                            setSnapshot((prev) => applyEvent(prev, {
                                kind: 'location',
                                point: {
                                    lat: pos.coords.latitude,
                                    lng: pos.coords.longitude,
                                    accuracy: pos.coords.accuracy,
                                    at: nowSec(),
                                },
                            }));
                        }
                    },
                );
            } catch (err) {
                console.warn('Failed to start GPS tracking', err);
            }
        };

        void startTracking();

        return () => {
            cancelled = true;
            if (watchId !== null) void Geolocation.clearWatch({ id: watchId });
        };
    }, [snapshot.state]);

    async function connect(acceptAll = false) {
        setEchoResult(null);
        setActivityResult(null);
        setStatusMessage(null);
        const driver = await createDefaultElm327Driver({ acceptAllDevices: acceptAll });
        attachDriver(driver);
        await driver.connect();
    }

    async function disconnect() {
        await driverRef.current?.disconnect();
        setSnapshot(emptySnapshot());
    }

    function startTrip() {
        setSnapshot((prev) => applyEvent(prev, { kind: 'start', at: nowSec() }));
        setEchoResult(null);
        setActivityResult(null);
    }

    function endTrip() {
        setSnapshot((prev) => applyEvent(prev, { kind: 'end', at: nowSec() }));
    }

    async function resolveTripNode(target: TripNodeTarget): Promise<string> {
        if (target.kind === 'existing') return target.nodeId;
        return (await api.nodeUpsert({
            lat: target.lat,
            lng: target.lng,
            name: target.name,
            locality: target.locality,
        })).node_id;
    }

    async function saveTrip(target: TripNodeTarget): Promise<ActivityDriverTripResponse | null> {
        if (snapshot.state !== 'ended') return null;
        setActivityBusy(true);
        try {
            const nodeId = await resolveTripNode(target);
            const summary = snapshotToSummary(snapshot);
            const persona = inferPersona(summary, { started_at: snapshot.started_at ?? nowSec() });
            const r = await api.activityDriverTrip({
                node_id: nodeId,
                occurred_at: snapshot.ended_at ?? nowSec(),
                smoothness: summary.smoothness,
                hard_brakes: summary.hard_brakes,
                max_speed_bucket: speedToBucket(summary.max_speed_kmh),
                distance_bucket: distanceToBucket(summary.distance_km),
                duration_bucket: persona.duration_bucket,
                time_bucket: persona.time_bucket,
                avg_rpm_bucket: rpmToBucket(summary.avg_rpm),
                throttle_bucket: throttleToBucket(summary.avg_throttle_pct),
            });
            setActivityResult(r);
            return r;
        } catch (err) {
            setStatusMessage((err as Error).message);
            return null;
        } finally {
            setActivityBusy(false);
        }
    }

    async function uploadEcho(target: TripNodeTarget, activityEventId?: string) {
        if (snapshot.state !== 'ended') return;
        setEchoBusy(true);
        try {
            const nodeId = await resolveTripNode(target);
            const summary = snapshotToSummary(snapshot);
            const persona = inferPersona(summary, { started_at: snapshot.started_at ?? nowSec() });
            const r = await api.echoShare(musicVibe.attachToEcho({
                node_id: nodeId,
                activity_event_id: activityEventId,
                qualitative: persona.qualitative.length > 0 ? persona.qualitative : ['calm'],
                discomfort: persona.discomfort.length > 0 ? persona.discomfort : undefined,
                duration_bucket: persona.duration_bucket,
                time_bucket: persona.time_bucket,
            }));
            setEchoResult({
                trace_id: r.trace_id,
                completed_quests: r.quest_deltas
                    .filter((q) => q.completedNow)
                    .map((q) => ({ title: q.title, rewards: q.rewards })),
            });
        } catch (err) {
            setStatusMessage((err as Error).message);
        } finally {
            setEchoBusy(false);
        }
    }

    function reset() {
        setSnapshot((prev) => applyEvent(prev, { kind: 'reset' }));
        setEchoResult(null);
        setActivityResult(null);
    }

    const summary = snapshot.state === 'ended' ? snapshotToSummary(snapshot) : null;
    const faceBaseProps = palProfileToFaceProps(pal);
    const driveFaceState = useDriveFaceState(snapshot, status);
    const orientationMode = useOrientationMode();
    const nowPlaying = useNowPlaying();
    const latestRoutePoint = snapshot.route[snapshot.route.length - 1] ?? null;
    const musicH3Cell = snapshot.state === 'driving' && latestRoutePoint
        ? latLngToCell(latestRoutePoint.lat, latestRoutePoint.lng, 7)
        : null;
    const musicVibe = useMusicVibe({
        musicVibeSharing: pal.profile.signal_toggles.music_vibe_sharing,
        driveState: snapshot.state === 'driving' ? 'CRUISING' : 'IDLE',
        h3Cell: musicH3Cell,
    });
    const ambientMode = musicVibe.nearbySameGenrePing && allowsSocialAmbient(driveFaceState)
        ? AmbientMode.NOTES
        : AmbientMode.NONE;
    const voiceOptIn = useVoiceOptIn();
    const voiceDeps = useMemo(() => ({
        renderVoice: renderVoiceTurn,
        appendTurn: appendVoiceTurn,
    }), [appendVoiceTurn, renderVoiceTurn]);
    const voiceSession = useVoiceSession(voiceDeps);
    useWakeLock(snapshot.state === 'driving');
    const showLandscapeCockpit = active && shouldShowLandscapeCockpit(snapshot, orientationMode === 'landscape');
    const voiceMicVisible = shouldShowVoiceMic({
        supported: voiceOptIn.supported,
        optIn: voiceOptIn.optIn,
        permission: voiceOptIn.permission,
        driving: snapshot.state === 'driving',
        cockpitVisible: showLandscapeCockpit,
    });
    const cockpitAmbient = resolveVoiceAmbient(ambientMode, voiceSession.state.status);
    const vibeMapCenter = mapCenterFromSnapshot(snapshot);
    const hardBrakesRef = useRef(snapshot.hard_brakes);
    const previousTripStateRef = useRef(snapshot.state);
    const voiceStatusRef = useRef(voiceSession.state.status);

    useEffect(() => {
        voiceStatusRef.current = voiceSession.state.status;
    }, [voiceSession.state.status]);

    useEffect(() => {
        if (shouldInterruptForHardBrakeCount(hardBrakesRef.current, snapshot.hard_brakes)) {
            void voiceSession.interrupt('hard_brake');
        }
        hardBrakesRef.current = snapshot.hard_brakes;
    }, [snapshot.hard_brakes, voiceSession.interrupt]);

    useEffect(() => {
        if (previousTripStateRef.current === 'driving' && snapshot.state !== 'driving') {
            void voiceSession.interrupt('trip_end');
        }
        previousTripStateRef.current = snapshot.state;
    }, [snapshot.state, voiceSession.interrupt]);

    useEffect(() => {
        if (showLandscapeCockpit || voiceStatusRef.current === 'idle') return;
        void voiceSession.interrupt('cockpit_hidden');
    }, [showLandscapeCockpit, voiceSession.interrupt]);

    useEffect(() => {
        let disposed = false;
        let remove: (() => void) | null = null;
        void CapApp.addListener('appStateChange', (state) => {
            if (disposed || state.isActive || voiceStatusRef.current === 'idle') return;
            void voiceSession.interrupt('app_background');
        }).then((handle) => {
            remove = () => { void handle.remove(); };
            if (disposed) remove();
        });
        return () => {
            disposed = true;
            remove?.();
        };
    }, [voiceSession.interrupt]);

    useEffect(() => {
        const onVisibilityChange = () => {
            if (document.visibilityState !== 'hidden' || voiceStatusRef.current === 'idle') return;
            void voiceSession.interrupt('document_hidden');
        };
        document.addEventListener('visibilitychange', onVisibilityChange);
        return () => document.removeEventListener('visibilitychange', onVisibilityChange);
    }, [voiceSession.interrupt]);

    function handleVoicePress() {
        if (voiceSession.state.status === 'idle') {
            void voiceSession.start();
            return;
        }
        void voiceSession.cancel('manual_cancel');
    }

    if (!supported) {
        return <UnsupportedBrowserNotice />;
    }

    return (
        <>
            {showLandscapeCockpit ? (
                <LandscapeDriveDisplay
                    snapshot={snapshot}
                    status={status}
                    faceState={driveFaceState}
                    faceBaseProps={faceBaseProps}
                    ambient={cockpitAmbient}
                    nowPlaying={nowPlaying.current}
                    voiceControls={{
                        visible: voiceMicVisible,
                        state: voiceSession.state,
                        onPress: handleVoicePress,
                    }}
                    onEndTrip={endTrip}
                    onOpenMap={() => setShowVibeMap(true)}
                />
            ) : (
                <div className="space-y-4">
                    <div className="card">
                        <h3 className="font-semibold mb-2">Driving</h3>
                        <p className="muted text-sm mb-3">
                            Pair your ELM327 dongle to share calm-drive Echoes with the
                            network and tick driving Quests. Specific routes and exact
                            GPS never leave your phone.
                        </p>
                        <StatusRow status={status} message={statusMessage} />
                        <NowPlayingPermissionCard
                            supported={nowPlaying.supported}
                            enabled={nowPlaying.enabled}
                            loading={nowPlaying.loading}
                            onOpenSettings={() => { void nowPlaying.openSettings(); }}
                            onRefresh={() => { void nowPlaying.refresh(); }}
                        />
                        <div className="flex gap-2 mt-3">
                            {status !== 'connected' && status !== 'connecting' && status !== 'initialising' && (
                                <button className="btn-primary" onClick={() => connect(true)}>
                                    Pair dongle
                                </button>
                            )}
                            {status === 'connected' && (
                                <button className="btn-secondary" onClick={disconnect}>
                                    Disconnect
                                </button>
                            )}
                        </div>
                        {(status === 'connected' || status === 'error' || debugLogs.length > 0) && (
                            <div className="mt-3">
                                <div className="flex items-center gap-3 mb-2">
                                    <button
                                        type="button"
                                        className="muted text-xs underline"
                                        onClick={() => setShowDebug((d) => !d)}
                                    >
                                        {showDebug ? 'Hide debug log' : 'Show debug log'}
                                    </button>
                                    {debugLogs.length > 0 && (
                                        <button
                                            type="button"
                                            className="muted text-xs underline"
                                            onClick={shareLogs}
                                        >
                                            Share logs
                                        </button>
                                    )}
                                    {debugLogs.length > 0 && (
                                        <button
                                            type="button"
                                            className="muted text-xs underline"
                                            onClick={() => setDebugLogs([])}
                                        >
                                            Clear
                                        </button>
                                    )}
                                </div>
                                {showDebug && (
                                    <div className="bg-cockpit text-success font-mono text-[10px] p-2 rounded h-48 overflow-y-auto whitespace-pre-wrap break-all">
                                        {debugLogs.length === 0 ? 'No events yet.' : debugLogs.map((log, i) => (
                                            <div key={i}>{log}</div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                        {status !== 'connected' && status !== 'connecting' && status !== 'initialising' && (
                            <button
                                type="button"
                                className="muted text-xs mt-3 underline"
                                onClick={() => setShowTroubleshoot((v) => !v)}
                            >
                                {showTroubleshoot ? 'Hide tips' : "Don't see your dongle?"}
                            </button>
                        )}
                        {showTroubleshoot && <TroubleshootCard onScanAll={() => connect(true)} />}
                    </div>

                    {snapshot.state !== 'idle' && (
                        <LiveCard
                            snapshot={snapshot}
                            onStart={startTrip}
                            onEnd={endTrip}
                            status={status}
                            faceState={driveFaceState}
                            faceBaseProps={faceBaseProps}
                            ambient={ambientMode}
                            nowPlaying={nowPlaying.current}
                            onOpenMap={() => setShowVibeMap(true)}
                            active={active}
                        />
                    )}

                    {summary && !echoResult && (
                        <PostTripCard
                            summary={summary}
                            busy={echoBusy}
                            onUpload={uploadEcho}
                            onSave={saveTrip}
                            onSkip={reset}
                            pal={pal}
                            saveBusy={activityBusy}
                            activityResult={activityResult}
                        />
                    )}

                    {echoResult && <EchoResultCard result={echoResult} onClose={reset} />}
                </div>
            )}
            {showVibeMap && (
                <Suspense fallback={<div className="fixed inset-0 z-50 bg-surface p-5">Loading map...</div>}>
                    <VibeMap
                        onClose={() => setShowVibeMap(false)}
                        initialCenter={vibeMapCenter ?? undefined}
                    />
                </Suspense>
            )}
        </>
    );
}

function StatusRow({ status, message }: { status: DriverStatus; message: string | null }) {
    const label =
        status === 'idle' ? 'No dongle paired' :
        status === 'requesting' ? 'Waiting for selection...' :
        status === 'connecting' ? 'Connecting...' :
        status === 'initialising' ? 'Initialising...' :
        status === 'connected' ? 'Connected' :
        status === 'disconnected' ? 'Disconnected' :
        'Error';
    const dot =
        status === 'connected' ? 'bg-success' :
        status === 'error' ? 'bg-danger' :
        'bg-line';
    return (
        <div className="flex items-center gap-2 text-sm">
            <span className={`inline-block w-2 h-2 rounded-full ${dot}`} aria-hidden />
            <span>{label}</span>
            {message && <span className="muted text-xs">- {message}</span>}
        </div>
    );
}

function LiveCard({ snapshot, onStart, onEnd, status, faceState, faceBaseProps, ambient, nowPlaying, onOpenMap, active }: {
    snapshot: TripSnapshot;
    onStart: () => void;
    onEnd: () => void;
    status: DriverStatus;
    faceState: FaceState;
    faceBaseProps: FaceBaseProps;
    ambient: AmbientMode;
    nowPlaying: ReturnType<typeof useNowPlaying>['current'];
    onOpenMap: () => void;
    active: boolean;
}) {
    const driving = snapshot.state === 'driving';
    return (
        <div className="card">
            <div className="flex justify-between items-baseline mb-3">
                <h3 className="font-semibold">Trip</h3>
                {driving && <span className="text-xs px-2 py-0.5 rounded-full bg-success/30">live</span>}
            </div>
            <div className="mb-4 flex justify-center rounded-lg border border-line bg-cockpit p-2">
                <PalFaceCartoon
                    state={faceState}
                    form={faceBaseProps.form}
                    marks={faceBaseProps.marks}
                    accessory={faceBaseProps.accessory}
                    ambient={ambient}
                    active={active && driving}
                    className="shadow-sm"
                />
            </div>
            <NowPlayingChip track={nowPlaying} className="mb-3" />
            <div className="grid grid-cols-2 gap-2 text-sm">
                <Stat label="Speed" value={`${(snapshot.last_speed_kmh ?? 0).toFixed(0)} km/h`} />
                <Stat label="Max speed" value={`${snapshot.max_speed_kmh.toFixed(0)} km/h`} />
                <Stat label="RPM" value={snapshot.last_rpm !== null ? Math.round(snapshot.last_rpm).toLocaleString() : '-'} />
                <Stat label="Max RPM" value={snapshot.max_rpm > 0 ? Math.round(snapshot.max_rpm).toLocaleString() : '-'} />
                <Stat label="Throttle" value={snapshot.last_throttle_pct !== null ? `${snapshot.last_throttle_pct.toFixed(0)}%` : '-'} />
                <Stat label="Max throttle" value={snapshot.max_throttle_pct > 0 ? `${snapshot.max_throttle_pct.toFixed(0)}%` : '-'} />
                <Stat label="Distance" value={`${snapshot.distance_km.toFixed(1)} km`} />
                <Stat label="Duration" value={formatDuration(snapshot.duration_s)} />
                <Stat label="Hard brakes" value={String(snapshot.hard_brakes)} />
                <Stat label="Moving" value={formatDuration(snapshot.moving_s)} />
            </div>
            {(driving || snapshot.route.length > 0) && (
                <div className="mt-4">
                    <TripRouteMap route={snapshot.route} label={driving ? 'Live route' : 'Recorded route'} />
                </div>
            )}
            <div className="flex gap-2 mt-4">
                {!driving && status === 'connected' && (
                    <button className="btn-primary" onClick={onStart}>Start trip</button>
                )}
                {driving && (
                    <button className="btn-secondary" onClick={onEnd}>End trip</button>
                )}
                <button className="btn-secondary" onClick={onOpenMap}>Map</button>
            </div>
        </div>
    );
}

function allowsSocialAmbient(state: FaceState): boolean {
    return state !== FaceState.SCARED
        && state !== FaceState.CHECK_ENGINE
        && state !== FaceState.OVERHEATING
        && state !== FaceState.LOW_FUEL;
}

export function resolveVoiceAmbient(baseAmbient: AmbientMode, voiceStatus: VoiceSessionStatus): AmbientMode {
    if (voiceStatus === 'listening') return AmbientMode.MIC;
    if (voiceStatus === 'thinking' || voiceStatus === 'speaking') return AmbientMode.NONE;
    return baseAmbient;
}

export function shouldShowVoiceMic(args: {
    supported: boolean;
    optIn: boolean;
    permission: string;
    driving: boolean;
    cockpitVisible: boolean;
}): boolean {
    return args.supported
        && args.optIn
        && args.permission === 'granted'
        && args.driving
        && args.cockpitVisible;
}

export function shouldInterruptForHardBrakeCount(previous: number, next: number): boolean {
    return next > previous;
}

function PostTripCard({ summary, busy, onUpload, onSave, onSkip, pal, saveBusy, activityResult }: {
    summary: ReturnType<typeof snapshotToSummary>;
    busy: boolean;
    onUpload: (target: TripNodeTarget, activityEventId?: string) => void;
    onSave: (target: TripNodeTarget) => Promise<ActivityDriverTripResponse | null>;
    onSkip: () => void;
    pal: PalProfileResponse;
    saveBusy: boolean;
    activityResult: ActivityDriverTripResponse | null;
}) {
    const sharingOn = pal.profile.signal_toggles.experience_sharing;
    const smoothPct = Math.round(summary.smoothness * 100);
    const rpmLine = summary.avg_rpm > 0
        ? ` - avg ${Math.round(summary.avg_rpm).toLocaleString()} RPM - peak ${Math.round(summary.max_rpm).toLocaleString()} RPM`
        : '';
    const throttleLine = summary.avg_throttle_pct > 0
        ? ` - avg throttle ${summary.avg_throttle_pct.toFixed(0)}%`
        : '';
    const statsLine = `${summary.distance_km.toFixed(1)} km in ${formatDuration(summary.duration_s)} - smoothness ${smoothPct}%${rpmLine}${throttleLine}`;

    const [resolved, setResolved] = useState<Extract<TripNodeTarget, { kind: 'resolved' }> | null>(null);
    const [detecting, setDetecting] = useState(sharingOn);
    const [showManual, setShowManual] = useState(!sharingOn);
    const [searchText, setSearchText] = useState('');
    const [results, setResults] = useState<NodeChoice[]>([]);
    const [chosenNode, setChosenNode] = useState('');
    const debounceRef = useRef<number | null>(null);

    useEffect(() => {
        if (!sharingOn) {
            setResolved(null);
            setDetecting(false);
            setShowManual(true);
            return;
        }

        let cancelled = false;
        setDetecting(true);

        const tryResolve = async (lat: number, lng: number) => {
            try {
                const resp = await fetch(
                    `https://nominatim.openstreetmap.org/reverse?lat=${lat.toFixed(6)}&lon=${lng.toFixed(6)}&format=json`,
                    { headers: { Accept: 'application/json' } },
                );
                const data = await resp.json() as {
                    address?: { road?: string; suburb?: string; quarter?: string; town?: string; city?: string };
                };
                const a = data.address;
                const name = a?.road ?? a?.suburb ?? a?.quarter ?? a?.town ?? a?.city ?? '';
                const locality = a?.town ?? a?.city ?? a?.suburb ?? undefined;
                if (!cancelled && name) {
                    setResolved({ kind: 'resolved', lat, lng, name, locality });
                }
            } catch {
                // User can still pick a route manually.
            }
            if (!cancelled) setDetecting(false);
        };

        const getCoords = async () => {
            if (summary.route.length > 0) {
                const mid = summary.route[Math.floor(summary.route.length / 2)]!;
                await tryResolve(mid.lat, mid.lng);
                return;
            }
            try {
                if (isNative()) {
                    const perm = await Geolocation.requestPermissions();
                    if (perm.location !== 'granted') {
                        if (!cancelled) setDetecting(false);
                        return;
                    }
                }
                const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy: false, timeout: 8000 });
                if (!cancelled) await tryResolve(pos.coords.latitude, pos.coords.longitude);
            } catch {
                if (!cancelled) setDetecting(false);
            }
        };

        void getCoords();
        return () => { cancelled = true; };
    }, [sharingOn, summary.route]);

    useEffect(() => {
        return () => {
            if (debounceRef.current !== null) clearTimeout(debounceRef.current);
        };
    }, []);

    function handleSearch(text: string) {
        setSearchText(text);
        setChosenNode('');
        if (debounceRef.current !== null) clearTimeout(debounceRef.current);
        debounceRef.current = window.setTimeout(async () => {
            const q = text.trim();
            if (q.length < 2) {
                setResults([]);
                return;
            }
            try {
                const r = await api.nodeSearch({ domain: 'route', q, limit: 8 });
                const items = r.items.map((n) => ({ id: n.id, name: n.name }));
                setResults(items);
                if (items[0]) setChosenNode(items[0].id);
            } catch {
                // Ignore transient search failures.
            }
        }, 350);
    }

    const manualTarget: TripNodeTarget | null = chosenNode
        ? { kind: 'existing', nodeId: chosenNode }
        : null;
    const activeTarget = showManual ? manualTarget : resolved;

    return (
        <div className="card">
            <h3 className="font-semibold mb-2">Trip done</h3>
            <p className="muted text-sm mb-3">{statsLine}</p>

            {!showManual && (
                <div className="mb-4">
                    {detecting ? (
                        <p className="muted text-sm">Detecting your location...</p>
                    ) : resolved ? (
                        <div className="flex items-center justify-between">
                            <p className="text-sm">
                                <strong>{resolved.name}</strong>
                                {resolved.locality && <span className="muted"> - {resolved.locality}</span>}
                            </p>
                            <button
                                type="button"
                                className="muted text-xs underline ml-2"
                                onClick={() => setShowManual(true)}
                            >
                                Change
                            </button>
                        </div>
                    ) : (
                        <p className="muted text-sm text-danger">
                            Couldn't detect location.{' '}
                            <button
                                type="button"
                                className="underline"
                                onClick={() => setShowManual(true)}
                            >
                                Search manually
                            </button>
                        </p>
                    )}
                </div>
            )}

            {showManual && (
                <div className="mb-4">
                    <p className="text-sm mb-2">Where should your Pal remember this?</p>
                    <input
                        type="text"
                        className="w-full p-2 rounded-lg border border-line mb-2 text-sm"
                        placeholder="Type a street or area name..."
                        value={searchText}
                        onChange={(e) => handleSearch(e.target.value)}
                        autoFocus={sharingOn}
                    />
                    {results.length > 0 && (
                        <div className="border border-line rounded-lg divide-y divide-line mb-2 overflow-hidden text-sm">
                            {results.map((n) => (
                                <button
                                    key={n.id}
                                    type="button"
                                    className={`w-full text-left px-3 py-2 ${chosenNode === n.id ? 'bg-brand/10 font-medium' : ''}`}
                                    onClick={() => setChosenNode(n.id)}
                                >
                                    {n.name}
                                </button>
                            ))}
                        </div>
                    )}
                    {results.length === 0 && searchText.trim().length >= 2 && (
                        <p className="muted text-xs mb-2">No routes found - try a different name.</p>
                    )}
                    {sharingOn && (
                        <button
                            type="button"
                            className="muted text-xs underline"
                            onClick={() => setShowManual(false)}
                        >
                            Back
                        </button>
                    )}
                </div>
            )}

            {activityResult && (
                <div className="bg-surface-raised rounded-lg p-3 text-sm mb-3">
                    <div className="font-medium mb-1">Saved to Pal</div>
                    {activityResult.memories.slice(0, 2).map((m) => (
                        <p key={m.id} className="muted">{m.summary}</p>
                    ))}
                    {activityResult.collections.length > 0 && (
                        <p className="muted text-xs mt-2">
                            {activityResult.collections[0]!.title}: {activityResult.collections[0]!.progress}/{activityResult.collections[0]!.target}
                        </p>
                    )}
                </div>
            )}

            {!sharingOn && (
                <p className="muted text-xs mb-3">
                    Experience sharing is off. Save keeps this private to your Pal, and location prefill stays off.
                </p>
            )}

            <div className="flex gap-2 mt-2">
                <button className="btn-secondary" onClick={onSkip}>Skip</button>
                <button
                    className="btn-secondary"
                    onClick={() => { if (activeTarget) void onSave(activeTarget); }}
                    disabled={saveBusy || !activeTarget || Boolean(activityResult)}
                >
                    {saveBusy ? 'Saving...' : activityResult ? 'Saved' : 'Save to Pal'}
                </button>
                <button
                    className="btn-primary"
                    onClick={() => { if (activeTarget) onUpload(activeTarget, activityResult?.event.id); }}
                    disabled={busy || !activeTarget || !sharingOn}
                >
                    {busy ? 'Sharing...' : 'Share Echo'}
                </button>
            </div>
        </div>
    );
}

function EchoResultCard({ result, onClose }: {
    result: NonNullable<ReturnType<typeof useState<{ trace_id: string; completed_quests: Array<{ title: string; rewards?: { glow?: number; marks?: string[] } }> }>>[0]>;
    onClose: () => void;
}) {
    return (
        <div className="card">
            <h3 className="font-semibold mb-2">Echo shared</h3>
            <p className="muted text-xs mb-2">trace - {result.trace_id.slice(0, 8)}</p>
            {result.completed_quests.length > 0 && (
                <div className="mb-3">
                    <p className="text-sm font-medium mb-1">Quests completed:</p>
                    {result.completed_quests.map((q, i) => (
                        <div key={i} className="text-sm">
                            {q.title}
                            {q.rewards?.glow && ` - +${q.rewards.glow} glow`}
                            {q.rewards?.marks && ` - +${q.rewards.marks.length} mark`}
                        </div>
                    ))}
                </div>
            )}
            <button className="btn-secondary" onClick={onClose}>Done</button>
        </div>
    );
}

function TroubleshootCard({ onScanAll }: { onScanAll: () => void }) {
    return (
        <div className="mt-3 p-3 bg-surface-raised rounded-lg text-sm space-y-2">
            <p className="font-medium">Common reasons your dongle isn't showing:</p>

            <p>
                <strong>1. The dongle is Bluetooth Classic, not BLE.</strong> Web
                Bluetooth only sees BLE. If your dongle shows in Android's regular
                Bluetooth settings but not here, it might be Classic. Check the
                product page for Bluetooth 4.0 BLE or Bluetooth Smart.
            </p>

            <p>
                <strong>2. Already paired to your phone OS.</strong> Web Bluetooth
                ignores devices already paired in OS settings on some Android
                builds. Forget the device in Android Bluetooth settings, then retry.
            </p>

            <p>
                <strong>3. Location permission off.</strong> Android requires
                Location to be enabled for BLE scanning, even if we do not use
                location for the BLE connection.
            </p>

            <p>
                <strong>4. The dongle's name doesn't match our filter.</strong>
                Tap below to scan all nearby BLE devices and find yours by name.
            </p>

            <button
                type="button"
                className="btn-secondary w-full mt-2"
                onClick={onScanAll}
            >
                Scan all BLE devices (diagnostic)
            </button>
        </div>
    );
}

function UnsupportedBrowserNotice() {
    return (
        <div className="card">
            <h3 className="font-semibold mb-2">Driving Pal</h3>
            <p className="muted text-sm">
                Driving needs Web Bluetooth, which isn't available in this browser.
                On Android, open Vibe Pal in Chrome. On iOS, the native Vibe Pal app
                bundles the Bluetooth bridge.
            </p>
        </div>
    );
}

function Stat({ label, value }: { label: string; value: string }) {
    return (
        <div className="bg-surface-raised rounded-lg p-2">
            <div className="muted text-xs">{label}</div>
            <div className="font-semibold">{value}</div>
        </div>
    );
}

function formatDuration(s: number): string {
    const mins = Math.floor(s / 60);
    const secs = Math.floor(s % 60);
    if (mins === 0) return `${secs}s`;
    if (mins < 60) return `${mins}m ${secs}s`;
    const hours = Math.floor(mins / 60);
    return `${hours}h ${mins % 60}m`;
}

function nowSec(): number { return Math.floor(Date.now() / 1000); }

function mapCenterFromSnapshot(snapshot: TripSnapshot): { lat: number; lng: number; zoom: number } | null {
    if (snapshot.route.length > 0) {
        const point = snapshot.route[Math.floor(snapshot.route.length / 2)]!;
        return { lat: point.lat, lng: point.lng, zoom: snapshot.state === 'driving' ? 15 : 13 };
    }
    return null;
}
